'use client'

import { useState, useEffect, useRef } from 'react'
import { X, MapPin, Pen } from 'lucide-react'
import dynamic from 'next/dynamic'
import useCommonStore from '@/store/commonStore'
import { toast } from '@/hooks/use-toast'


interface Address {
  address_id: string
  full_nm: string
  phn_num: string
  phn_cd: string
  email: string
  locality: string
  city: string
  state: string
  country: string
  zip_code: string
  is_default: boolean
  address_name: string
  address_line1: string
  address_line2: string
  lat: number
  lng: number
  loc_str: string
}

export function AddressModal({ open, onClose }: { open: boolean, onClose: () => void }) {
  const [show, setShow] = useState(open)
  const [showAll, setShowAll] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [locationMapOpen, setLocationMapOpen] = useState(false)
  const [addressId, setAddressId] = useState<string>("")
  const [loading, setLoading] = useState(false)
  const [selectedAddress, setSelectedAddress] = useState<Record<string, any>>({})
  const modalRef = useRef<HTMLDivElement>(null)
  const { setAddress, fetchAddressList, addressList, user } = useCommonStore()
  const { translations } = useCommonStore()


  const fetchAddresses = async () => {
    setLoading(true)
    const response = await fetchAddressList()
    setLoading(false)
    if (!response.status) {
      toast({
        title: "Error",
        description: response?.error || response.message,
        variant: "destructive"
      })
    }
  }

  useEffect(() => {
    if (open) {
      setShow(true)
      fetchAddresses()
    } else {
      const timeout = setTimeout(() => setShow(false), 300)
      return () => clearTimeout(timeout)
    }
  }, [open])

  if (!show) return null

  const addNew = () => {
    setAddressId("")
    setLocationMapOpen(true);
  }
  const selectAddress = async (address: Address) => { 
    setLoading(true)
    const result = await setAddress(address)
    setLoading(false)
    if(result.status) {
      onClose()
    }else{
      toast({
        title: "Error",
        description: result?.error||result.message,
        variant: "destructive"
      })
    }
  }

  const handleLocationConfirm = (doc: Record<string, any>,center: { lat: number, lng: number }) => {
    // console.log(JSON.stringify(doc));
    
    let obj={
      address_line2:"",
      locality:"",
      city:"",
      state:"",
      country:"",
      zip_code:"",
      lat:center.lat,
      lng:center.lng,
      loc_str:""
    }
    if (doc?.features?.length) {
      doc.features?.forEach((a: Record<string, any>) => {
        if(a.place_type.includes("address")){
          obj.address_line2=a.text;
          obj.loc_str=a.place_name;
        }
        if(a.place_type.includes("neighborhood")){
          obj.locality=a.text;
        }
        if (a.place_type.includes("locality") && !obj.locality) {
          obj.locality = a.text;
        }
        if(a.place_type.includes("place")){
          obj.city=a.text;
        }
        if(a.place_type.includes("region")){
          obj.state=a.text;
        }
        if(a.place_type.includes("country")){
          let code = a?.properties?.short_code.toUpperCase();
          obj.country=code?code:a.text;
        }
        if(a.place_type.includes("postcode")){
          obj.zip_code=a.text;
        }
      })
      if(!obj.locality){
        obj.locality=obj.city||""
      }
      if(!obj.loc_str){
        obj.loc_str=obj?.address_line2||""
      }
    }
    // console.log(obj);
    
    setSelectedAddress(obj);
    setLocationMapOpen(false);
    setModalOpen(true);
  }

  return (
    <div className={`fixed inset-0 bg-black/40 flex items-end md:items-center justify-center transition-opacity duration-300 z-[51] ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} style={{ zIndex: 99 }}>
      <div
        ref={modalRef}
        className={`bg-white w-full max-w-md rounded-t-2xl md:rounded-2xl pb-0 shadow-lg relative transition-transform duration-300
        ${open ? 'translate-y-0 md:scale-100' : 'translate-y-full md:scale-95'}`}
      >
        <div className='p-4 shadow-sm'>
        <button
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 rtl:left-4 rtl:right-auto"
          onClick={onClose}
        >
          <X className="w-6 h-6" />
        </button>
        <div className="font-bold text-lg">{translations['slct_delivery_add'] || "Select Delivery Address"}</div>
        </div>
        {addressList?.length > 0 ? (
          <>
            <div className="max-h-[75dvh] overflow-y-auto scroll-smooth" style={{ scrollBehavior: 'smooth' }}>
              {(showAll ? addressList : addressList.slice(0, 3)).map((addr: Address) => (
                <div onClick={() => selectAddress(addr)} key={addr.address_id} className={`p-4 border-b last:border-none relative ${user?.address?.address_id === addr.address_id ? 'bg-chart-1' : 'hover:bg-chart-1 cursor-pointer'}`}>
                  <div onClick={(e) => {
                    e.stopPropagation()
                    setAddressId(addr.address_id)
                    setLocationMapOpen(true)
                    setSelectedAddress({lat:addr.lat,lng:addr.lng})
                  }} className='absolute right-4 top-4 rtl:left-0 rtl:right-auto cursor-pointer'>
                    <div className='p-2 rounded-md bg-chart-2'><Pen className='w-4 h-4' /></div>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-5 h-5 text-gray-400" />
                    <span className="font-medium">{addr.address_name}</span>
                    <span className="bg-chart-2 text-xs px-2 py-0.5 rounded ml-2">{addr.zip_code}</span>
                    {user?.address?.address_id === addr.address_id && <span className='text-xs bg-green-500 text-white rounded px-1 z-[103]'>{translations["default"] || "Default"}</span>}
                  </div>
                  <div className="text-sm text-gray-700 ml-7 truncate">
                    <span>{addr.full_nm}, </span>
                    <span>{addr.address_line1}, </span>
                    <span>{addr.address_line2}, </span>
                    <span>{addr.locality}, </span>
                    <span>{addr.city}, </span>
                  </div>
                </div>
              ))}
            </div>
            {addressList?.length > 3 && <button
              className="text-blue-600 text-sm mt-2 mb-2 ml-1"
              onClick={() => setShowAll(v => !v)}
            >
              {showAll ? 'Show less' : 'See more'}
            </button>}
          </>
        ) : (
          <div className="text-sm text-gray-500 min-h-[100px] flex justify-center items-center">
            {translations['empty_address'] || "No addresses found"}
          </div>
        )}

        <div className="shadow p-4 border-t">
          <button
            className="w-full bg-primary text-white py-2 rounded font-semibold"
            onClick={addNew}
          >
            + {translations['add_nw_addr'] || "Add New Address"}
          </button>
        </div>
      </div>

      {modalOpen && (
        <AddAddress
        translations={translations}
          open={modalOpen}
          onClose={() => {
            setModalOpen(false)
            setAddressId("")
             setSelectedAddress({})
          }}
          onBack={()=>{
            setModalOpen(false)
            setLocationMapOpen(true)
          }}
          addressId={addressId} // Pass null or a specific address ID if editing
          selectedAddress={selectedAddress??{}}
        />
      )}

      {/* Location Map Modal */}
      {locationMapOpen && (
        <LocationMap
          open={locationMapOpen}
          onClose={() => {
            setAddressId("")
            setSelectedAddress({})
            setLocationMapOpen(false)
          }}
          onConfirm={handleLocationConfirm}
          initialLat={selectedAddress?.lat || parseFloat(process.env.NEXT_PUBLIC_DEAFULT_LATITUDE || "28.6139")}
          initialLng={selectedAddress?.lng || parseFloat(process.env.NEXT_PUBLIC_DEAFULT_LONGITUDE || "77.209")}
        />
      )}
    </div>
  )
}

const AddAddress = dynamic(
  () => import('./AddAddress').then((mod) => mod.AddAddress),
  { ssr: false }
)

const LocationMap = dynamic(
  () => import('./LocationMap'),
  { ssr: false }
)
